import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="aegis-navy-bg text-white pt-20 pb-10"
    >
      <div className="container-x grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#3D7A73] flex items-center justify-center font-display font-bold">
              A
            </div>
            <div>
              <div className="font-display font-semibold text-xl">Aegis Care</div>
              <div className="text-xs uppercase tracking-widest text-white/60">
                Operations LTD
              </div>
            </div>
          </div>
          <p className="mt-6 text-white/70 leading-relaxed">
            Premium home care for Alberta families — built on dignity, trust,
            and the kind of compassion that feels like family.
          </p>
          <div className="mt-6 flex gap-3">
            <SocialIcon Icon={Facebook} label="facebook" />
            <SocialIcon Icon={Instagram} label="instagram" />
            <SocialIcon Icon={Linkedin} label="linkedin" />
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display font-semibold text-base mb-5">Services</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li><a href="#services" className="hover:text-[#9BD3CB]">Personal Care</a></li>
            <li><a href="#services" className="hover:text-[#9BD3CB]">Companionship</a></li>
            <li><a href="#services" className="hover:text-[#9BD3CB]">Specialized Care</a></li>
            <li><a href="#cdhci" className="hover:text-[#9BD3CB]">CDHCI Billing</a></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display font-semibold text-base mb-5">Company</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li><a href="#process" className="hover:text-[#9BD3CB]">Our Process</a></li>
            <li>
              <a
                href="#careers"
                data-testid="footer-careers-link"
                className="hover:text-[#9BD3CB]"
              >
                Careers / Hiring
              </a>
            </li>
            <li><a href="#contact" className="hover:text-[#9BD3CB]">Contact</a></li>
          </ul>
        </div>

        <div className="md:col-span-4" id="coverage">
          <h4 className="font-display font-semibold text-base mb-5">Get in touch</h4>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex gap-3" data-testid="footer-phone">
              <Phone size={18} className="text-[#9BD3CB] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <a href="tel:+17809946439" className="hover:text-[#9BD3CB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9BD3CB] rounded">
                24/7 Line: 780-994-6439
              </a>
            </li>
            <li className="flex gap-3" data-testid="footer-email">
              <Mail size={18} className="text-[#9BD3CB] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <a href="mailto:info@aegishomecare.ca" className="hover:text-[#9BD3CB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9BD3CB] rounded">
                info@aegishomecare.ca
              </a>
            </li>
            <li className="flex gap-3" data-testid="footer-address">
              <MapPin size={18} className="text-[#9BD3CB] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <address className="not-italic">
                2613 Wheaton Close NW<br />
                Edmonton, Alberta T6W 2X5
              </address>
            </li>
            <li className="flex gap-3" data-testid="footer-coverage">
              <MapPin size={18} className="text-[#9BD3CB] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                Proudly serving Edmonton, Calgary, and surrounding Alberta
                regions.
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="container-x border-t border-white/10 mt-14 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-sm text-white/60 text-center md:text-left">
          <div>© {new Date().getFullYear()} Aegis Care Operations LTD. All rights reserved.</div>
          <div className="mt-1 text-xs text-white/50" data-testid="footer-legal-ids">
            Business Registration No.: <span className="text-white/70">772874178</span>
            <span className="mx-2">•</span>
            Licence No.: <span className="text-white/70">2028274351</span>
          </div>
        </div>
        <div className="text-sm text-white/70 flex flex-wrap gap-6 justify-center md:justify-end">
          <Link
            to="/privacy"
            data-testid="footer-privacy-link"
            className="hover:text-[#9BD3CB] focus-visible:outline-2 focus-visible:outline-[#9BD3CB] focus-visible:outline-offset-2 rounded"
          >
            Privacy
          </Link>
          <Link
            to="/privacy"
            data-testid="footer-terms-link"
            className="hover:text-[#9BD3CB] focus-visible:outline-2 focus-visible:outline-[#9BD3CB] focus-visible:outline-offset-2 rounded"
          >
            Terms
          </Link>
          <Link
            to="/privacy"
            data-testid="footer-accessibility-link"
            className="hover:text-[#9BD3CB] focus-visible:outline-2 focus-visible:outline-[#9BD3CB] focus-visible:outline-offset-2 rounded"
          >
            Accessibility
          </Link>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ Icon, label }) => (
  <a
    href={`#${label}`}
    data-testid={`footer-social-${label}`}
    aria-label={label}
    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 hover:border-white/40 transition-colors"
  >
    <Icon size={16} />
  </a>
);
